import React from 'react';
import { observer } from 'mobx-react';
import { AppUI, ExerciseUI } from '@seedlang/state';
import Spinner from 'components/spinner';
import styled from '@emotion/styled';
import YoutubePlayer from 'components/worksheet/youtube_player';
import PodcastPlayer from 'components/worksheet/podcast_player';
import Stack, { gaps } from 'components/layout/stack';
import { observable } from 'mobx';
import ExerciseNavigation from './exercise_navigation';
import { Link } from 'react-router';
import WorksheetSubtitleRow from './worksheet_subtitle_row';
import WorksheetTabs from './worksheet_tabs';
import { LearningPathWorksheetStore } from '@seedlang/stores';

const Wrapper = styled.div`
  padding: ${gaps.md};
  background: white;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: ${gaps.md};
  width: 100%;
  max-width: 580px;
  animation: slideUp 0.2s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Title = styled(Link)`
  font-size: 28px;
  line-height: 32px;
  font-weight: 900;

  &:hover {
    text-decoration: underline;
  }
`;

const VideoThumbnailButton = styled.button`
  position: relative;
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.2s linear;
  
  &:hover {
    opacity: 0.9;
  }
`;

const VideoThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const YoutubePlayButton = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 68px;
  z-index: 1;
`;

const SpinnerWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: ${gaps.md} 0;
`;

@observer
class WorksheetPreview extends React.Component {
  @observable showVideo = false;
  @observable lastWorksheetId = null;

  componentDidMount() {
    this.lastWorksheetId = ExerciseUI.worksheet?.id;
  }

  componentDidUpdate() {
    if (ExerciseUI.worksheet?.id !== this.lastWorksheetId) {
      this.showVideo = false;
      this.lastWorksheetId = ExerciseUI.worksheet?.id;
    }
  }

  render() {
    if (ExerciseUI.worksheetStore.hasOutstandingRequests || !ExerciseUI.hasWorksheet) {
      return (
        <Wrapper>
          <Spinner />
        </Wrapper>
      );
    }

    return (
      <Wrapper key={ExerciseUI.worksheet.id}>
        {
          ExerciseUI.formattedYoutubeUrl && (
            this.showVideo ? (
              <YoutubePlayer
                allowFloating={false}
                hasAccess={ExerciseUI.hasCurrentWorksheetAccess}
                url={ExerciseUI.formattedYoutubeUrl}
                placeholderImageUrl={ExerciseUI.worksheet?.image?.url}
                podcastEpisode={ExerciseUI.worksheet.podcastEpisode}
                autoPlay
              />
            ) : (
              <VideoThumbnailButton onClick={() => this.showVideo = true} title='Play video'>
                <YoutubePlayButton src='/images/youtube_play_button.svg' alt='YouTube play button' />
                <VideoThumbnail src={ExerciseUI.worksheet?.imageUrl} alt='Video thumbnail' />
              </VideoThumbnailButton>
            )
          )
        }
        {
          ExerciseUI.worksheet.podcastEpisode && !ExerciseUI.formattedYoutubeUrl &&
            <PodcastPlayer
              hasAccess={ExerciseUI.hasCurrentWorksheetAccess}
              podcast={ExerciseUI.worksheet.podcastEpisode.podcast}
              podcastEpisode={ExerciseUI.worksheet.podcastEpisode}
            />
        }
        <Stack gap='xs' alignItems='stretch'>
          <Title to={{name: 'learn.show', params: {worksheetId: ExerciseUI.worksheet.id}}}>
            {ExerciseUI.worksheet.nameWithPublished}
          </Title>
          <WorksheetSubtitleRow />
        </Stack>
        <Stack gap='sm' alignItems='stretch'>
          {
            LearningPathWorksheetStore.hasOutstandingRequests ? (
              <SpinnerWrapper>
                <Spinner />
              </SpinnerWrapper>
            ) : (
              <Stack inline gap='xs'>
                <WorksheetTabs
                  worksheetId={ExerciseUI.worksheet.id}
                  params={ExerciseUI.routeStore.params}
                  showCustomTabs={false}
                  small
                  activeColor='#FFF'
                  activeBackground={AppUI.site.accentColor}
                  alwaysShowDiscuss
                  alwaysShowDownloads
                />
              </Stack>
            )
          }
          <ExerciseNavigation
            padding='0'
            exercises={ExerciseUI.worksheetStore.showData.exercises}
            worksheetId={ExerciseUI.worksheet?.id}
            useWorksheetRoute
          />
        </Stack>
      </Wrapper>
    );
  }
}

export default WorksheetPreview;
